import { Injectable } from '@angular/core';
import { Observable, Subject, Subscription, timer } from 'rxjs';
import { RefreshTokenData } from '../../models/session/refresh-token-request.interface';
import { HttpClient } from '@angular/common/http';
import { shareReplay, switchMap, takeUntil, retry } from 'rxjs/operators';
import { LocalStorageService, constants } from '@edassist/common';

@Injectable({
	providedIn: 'root'
})
export class TokenService {
	private tokenObservable: Observable<RefreshTokenData>;
	private tokenSubscription: Subscription;
	private stopTimer: Subject<boolean>;

	private readonly REFRESH_INTERVAL = 270000; // 4.5 minutes
	set token(token: string) {
		LocalStorageService.setItem(
			constants.localStorageKeys.SESSION_TOKEN,
			token
		);
		// Only set keep alive interval if token observable not already active
		if (token && !this.tokenObservable) {
			this.setTokenRefreshInterval();
		}
	}

	get token(): string {
		// Need to return empty string or HTTP Interceptor will bomb on a null value and cause console errors
		// and app won't load
		return (
			LocalStorageService.getItem(constants.localStorageKeys.SESSION_TOKEN) ||
			''
		); // tslint:disable-line
	}

	constructor(private readonly http: HttpClient) {}

	setTokenRefreshInterval(): void {
		const stopTimer = new Subject<boolean>();
		this.stopTimer = stopTimer;
		const timerObservable = timer(this.REFRESH_INTERVAL, this.REFRESH_INTERVAL);
		this.tokenObservable = timerObservable.pipe(
			takeUntil(stopTimer),
			switchMap(() => this.refreshToken().pipe(retry({ delay: 5000, count: 3 }))),
			shareReplay(1)
		);
		this.tokenSubscription = this.tokenObservable.subscribe({
			next: (newTokens) => {
				this.token = newTokens.sessionToken;
			},
			// Error handler for refresh call
			error: () => {
				console.error('Token refresh failed after 3 attempts.');
				this.clear();
				this.setTokenRefreshInterval();
			}
		});
	}

	private clear(): void {
		this.tokenSubscription?.unsubscribe();
		this.stopTimer?.next(true);
		this.stopTimer = undefined;
		this.tokenObservable = undefined;
	}

	private refreshToken(): Observable<RefreshTokenData> {
		const tokenData: RefreshTokenData = {
			sessionToken: this.token,
			refreshToken: LocalStorageService.getItem(
				constants.localStorageKeys.REFRESH_TOKEN
			)
		};

		return this.http.post<RefreshTokenData>(
			'sessions/refresh-token',
			tokenData
		);
	}
}
